import { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ImportUpload } from '@/components/import/import-upload';
import { ImportProgressCard } from '@/components/import/import-progress-card';
import { useImportJobs } from '@/hooks/use-import-jobs';
import type { ImportJob } from '@/types/import';
import { importService } from '@/services/import-service';
import { Loader2, Activity, CheckCircle2, XCircle, Upload } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

export default function ImportsPage(): React.ReactElement {
  const { jobs, activeJobs, completedJobs, failedJobs, loading, error, refetch } = useImportJobs();
  const [activeTab, setActiveTab] = useState('upload');

  const handleUploadSuccess = (job: ImportJob) => {
    setActiveTab('active');
    refetch();
  };

  const handleCancelJob = async (jobId: string) => {
    try {
      await importService.cancelJob(jobId);
      refetch();
    } catch (error) {
      console.error('Failed to cancel job:', error);
    }
  };

  const handleDeleteJob = async (jobId: string) => {
    try {
      await importService.deleteJob(jobId);
      refetch();
    } catch (error) {
      console.error('Failed to delete job:', error);
    }
  };

  const handleClearHistory = async () => {
    if (!window.confirm('Are you sure you want to clear all completed and failed imports? This action cannot be undone.')) {
      return;
    }
    try {
      await importService.clearHistory();
      refetch();
    } catch (error) {
      console.error('Failed to clear history:', error);
    }
  };

  const historyJobs = [...completedJobs, ...failedJobs].sort(
    (a, b) => new Date(b.startedDate ?? b.createdDate).getTime() - new Date(a.startedDate ?? a.createdDate).getTime()
  );

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b bg-card p-6">
        <div className="flex items-center gap-2">
          <Upload className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-bold tracking-tight">Import Assets</h1>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          Bulk import assets from CSV or Excel files
        </p>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        <div className="space-y-6 p-6">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center justify-between gap-4">
          <TabsList>
            <TabsTrigger value="upload">Upload</TabsTrigger>
            <TabsTrigger value="active">
              Active {activeJobs.length > 0 && `(${activeJobs.length})`}
            </TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
          </TabsList>
          
          <div className="flex gap-2 flex-shrink-0">
            {/* Active Pill */}
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-100 dark:bg-blue-900/20">
              <Activity className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
              <div className="flex items-baseline gap-1.5">
                <p className="text-xs font-medium text-blue-700 dark:text-blue-300">Active</p>
                <p className="text-sm font-bold text-blue-700 dark:text-blue-300">{activeJobs.length}</p>
              </div>
            </div>
            
            {/* Completed Pill */}
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-100 dark:bg-green-900/20">
              <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400 flex-shrink-0" />
              <div className="flex items-baseline gap-1.5">
                <p className="text-xs font-medium text-green-700 dark:text-green-300">Completed</p>
                <p className="text-sm font-bold text-green-700 dark:text-green-300">{completedJobs.length}</p>
              </div>
            </div>
            
            {/* Failed Pill */}
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-100 dark:bg-red-900/20">
              <XCircle className="h-4 w-4 text-red-600 dark:text-red-400 flex-shrink-0" />
              <div className="flex items-baseline gap-1.5">
                <p className="text-xs font-medium text-red-700 dark:text-red-300">Failed</p>
                <p className="text-sm font-bold text-red-700 dark:text-red-300">{failedJobs.length}</p>
              </div>
            </div>
          </div>
        </div>

        <TabsContent value="upload" className="space-y-4">
          <ImportUpload onUploadSuccess={handleUploadSuccess} />
        </TabsContent>

        <TabsContent value="active" className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : activeJobs.length === 0 ? (
            <Card>
              <CardContent className="py-12">
                <div className="text-center text-muted-foreground">
                  <p>No active imports</p>
                  <p className="text-sm mt-1">Upload a CSV file to start importing assets</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {activeJobs.map((job) => (
                <ImportProgressCard
                  key={job.id}
                  job={job}
                  onCancel={handleCancelJob}
                  onComplete={refetch}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="history" className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : historyJobs.length === 0 ? (
            <Card>
              <CardContent className="py-12">
                <div className="text-center text-muted-foreground">
                  <p>No import history</p>
                  <p className="text-sm mt-1">Completed and failed imports will appear here</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearHistory}
                >
                  Clear All History
                </Button>
              </div>
              <div className="space-y-4">
                {historyJobs.map((job) => (
                  <ImportProgressCard
                    key={job.id}
                    job={job}
                    onComplete={refetch}
                    onDelete={handleDeleteJob}
                  />
                ))}
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>
        </div>
      </div>
    </div>
  );
}
